import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import './Dashboard.css';
import ProfileModal from '../components/profileModal.js';

// ─── Helpers jadwal ─────────────────────────────────────────────────────────
const toTime = (iso) => (iso ? new Date(iso).getTime() : null);

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return new Intl.DateTimeFormat('id-ID', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return '-';
  }
};

const formatCountdown = (ms) => {
  if (ms == null || ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days}h ${hours}j ${minutes}m`;
  if (hours > 0) return `${hours}j ${minutes}m ${seconds}d`;
  if (minutes > 0) return `${minutes}m ${seconds}d`;
  return `${seconds} detik`;
};

const getScheduleState = (module, nowMs) => {
  const start = toTime(module?.scheduled_start_at);
  const end = toTime(module?.scheduled_end_at);

  if (!start && !end) return 'no_schedule'; 
  if (start && nowMs < start) return 'upcoming';
  if (end && nowMs > end) return 'expired';
  return 'open';
};

const isScheduleOpen = (module, nowMs) => {
  const state = getScheduleState(module, nowMs);
  return state === 'no_schedule' || state === 'open';
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [exams, setExams] = useState([]);
  const [psychotestStatus, setPsychotestStatus] = useState({
    completed: false,
    result: null,
    module_status: 'pending',
    scheduled_start_at: null,
    scheduled_end_at: null,
    auto_open: 1,
    is_active: 1,
    is_available: true,
    availability_reason: null,
  });
  const [showPsychotestResult, setShowPsychotestResult] = useState(false);
  const [loadingExams, setLoadingExams] = useState(true);
  const [startingId, setStartingId] = useState(null);
  const [error, setError] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());

  const [profileChecked, setProfileChecked] = useState(false);
const [needsProfile, setNeedsProfile] = useState(false);

useEffect(() => {
  const checkProfile = async () => {
    try {
      const res = await API.get('/candidate/profile');
      setNeedsProfile(!res.data?.profile);
    } catch (err) {
      console.error('Failed to check profile:', err);
      setNeedsProfile(false); // fail-open, jangan block kalau API error
    } finally {
      setProfileChecked(true);
    }
  };
  checkProfile();
}, []);

  // ─── Fetch daftar ujian ──────────────────────────────────────────────────────
  const fetchExams = useCallback(async () => {
    try {
      const [examRes, psyRes] = await Promise.all([
        API.get('/exam/list'),
        API.get('/psychotest/status'),
      ]);
      setExams(examRes.data);
      setPsychotestStatus({
        completed: !!psyRes.data?.completed,
        result: psyRes.data?.result || null,
        module_status: psyRes.data?.module_status || 'pending',
        scheduled_start_at: psyRes.data?.scheduled_start_at || null,
        scheduled_end_at: psyRes.data?.scheduled_end_at || null,
        auto_open: psyRes.data?.auto_open == null ? 1 : Number(psyRes.data.auto_open),
        is_available: psyRes.data?.is_available == null ? true : !!psyRes.data.is_available,
        is_active: psyRes.data?.is_active == null ? 1 : Number(psyRes.data.is_active),
        availability_reason: psyRes.data?.availability_reason || null,
      });
      setShowPsychotestResult(false);
    } catch (err) {
      setError('Gagal memuat daftar ujian');
    } finally {
      setLoadingExams(false);
    }
  }, []);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  // ─── Jam realtime (tick tiap 1 detik) ────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchExams, 60000);
    return () => clearInterval(interval);
  }, [fetchExams]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchExams();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
    };
  }, [fetchExams]);

  useEffect(() => {
    const token = localStorage.getItem('token'); // sesuaikan dengan cara API.js menyimpan token
    if (!token) return;

    const apiBase = String(process.env.REACT_APP_API_URL || '/api').replace(/\/?$/, '');
    const eventSource = new EventSource(`${apiBase}/candidate/schedule-events?token=${encodeURIComponent(token)}`);

    eventSource.addEventListener('schedule-updated', () => {
      fetchExams();
    });

    eventSource.onerror = () => {
    };

    return () => eventSource.close();
  }, [fetchExams]);

  useEffect(() => {
    const upcomingStarts = [
      psychotestStatus.scheduled_start_at,
      ...exams.map((exam) => exam.scheduled_start_at),
    ]
      .map(toTime)
      .filter((t) => t && t > Date.now());

    if (upcomingStarts.length === 0) return undefined;

    const nextStart = Math.min(...upcomingStarts);
    const delay = nextStart - Date.now();

    if (delay <= 0 || delay > 2147483647) return undefined;

    const timeout = setTimeout(() => {
      window.location.reload();
    }, delay + 250);

    return () => clearTimeout(timeout);
  }, [exams, psychotestStatus]);


const isExamUnlocked = (exam, blockedByPrevious) => {
  const completed = ['finished', 'failed', 'disqualified'].includes(
    exam.session_status
  );

  if (completed) return true;
  if (blockedByPrevious) return false;
  if (exam.is_available === false) return false;
  if (!isScheduleOpen(exam, nowMs)) return false;

  return true;
};

  const isPsychotestUnlocked = (blockedByPreviousFailure) => {
    if (blockedByPreviousFailure) return false;
    if (psychotestStatus.module_status === 'disqualified') return false;
    if (psychotestStatus.is_available === false) return false;
    if (!isScheduleOpen(psychotestStatus, nowMs)) return false;
    return true;
  };

  const isModuleFailed = (m) => {
    if (m.type === 'psychotest') {
      return ['failed', 'disqualified'].includes(psychotestStatus.module_status);
    }
    return ['failed', 'disqualified'].includes(m.exam.session_status);
  };

const getOrderedModules = () => {
  const modules = [
    ...(Number(psychotestStatus.is_active) === 1
      ? [{
          type: 'psychotest',
          key: 'psychotest',
          scheduled_start_at: psychotestStatus.scheduled_start_at,
          is_active: 1,
        }]
      : []),
    ...exams
      .filter(exam => Number(exam.is_active) === 1)
      .map(exam => ({
        type: 'exam',
        key: exam.id,
        scheduled_start_at: exam.scheduled_start_at,
        exam
      }))
  ];

  return modules
    .map((m, idx) => ({
      ...m,
      _sortTime: m.scheduled_start_at ? toTime(m.scheduled_start_at) : -Infinity,
      _idx: idx
    }))
    .sort((a, b) => a._sortTime - b._sortTime || a._idx - b._idx);
};

  // ─── Mulai / lanjut ujian ────────────────────────────────────────────────────
  const startExam = useCallback(
    async (exam_id) => {
      setStartingId(exam_id);
      setError('');
      try {
        const res = await API.post('/exam/start', { exam_id });
        navigate(`/exam/${res.data.session_id}`);
      } catch (err) {
        setError(err.response?.data?.message || 'Gagal memulai ujian');
        setStartingId(null);
      }
    },
    [navigate]
  );

  // ─── Format durasi ────────────────────────────────────────────────────────────
  const formatDuration = (minutes) => {
    if (!minutes) return '-';
    if (minutes < 60) return `${minutes} Menit`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h} Jam ${m} Menit` : `${h} Jam`;
  };

  // ─── Status badge ujian ───────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    if (!status) return null;
    const map = {
      active: { label: '▶ Sedang Berlangsung', cls: 'badge-active' },
      finished: { label: '✅ Selesai', cls: 'badge-finished' },
      failed: { label: '❌ Tidak Lulus', cls: 'badge-failed' },
      disqualified: { label: '🚫 Didiskualifikasi', cls: 'badge-disq' },
    };
    const s = map[status];
    if (!s) return null;
    return <span className={`exam-badge ${s.cls}`}>{s.label}</span>;
  };

  // ─── Badge & teks jadwal (dipakai untuk psikotest maupun exam) ───────────────
  const renderScheduleInfo = (module) => {
    const state = getScheduleState(module, nowMs);
    if (state === 'no_schedule') return null;

    const start = toTime(module.scheduled_start_at);
    const end = toTime(module.scheduled_end_at);

    if (state === 'upcoming') {
      return (
        <div className="schedule-box">
          <div className="schedule-row">
            <span className="info-label">🕐 Jadwal Mulai</span>
            <span className="info-val">{formatDateTime(module.scheduled_start_at)}</span>
          </div>
          {module.scheduled_end_at && (
            <div className="schedule-row">
              <span className="info-label">🏁 Jadwal Selesai</span>
              <span className="info-val">{formatDateTime(module.scheduled_end_at)}</span>
            </div>
          )}
          <div className="countdown-text">
            ⏳ Dibuka dalam {formatCountdown(start - nowMs)}
          </div>
        </div>
      );
    }

    if (state === 'expired') {
      return (
        <div className="schedule-box schedule-expired">
          <span className="info-label">⛔ Waktu telah berakhir</span>
          <span className="info-val">
            Jadwal ditutup pada {formatDateTime(module.scheduled_end_at)}
          </span>
        </div>
      );
    }

    return (
      <div className="schedule-box schedule-open">
        <span className="info-label">🟢 Sedang dibuka</span>
        {module.scheduled_end_at && (
          <span className="info-val">
            Ditutup {formatDateTime(module.scheduled_end_at)} (
            {formatCountdown(end - nowMs)} lagi)
          </span>
        )}
      </div>
    );
  };

  // ─── Label tombol ─────────────────────────────────────────────────────────────
  const getButtonLabel = (
    exam,
    isStarting,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
) => {

    if (isStarting) return 'Memuat...';

    if (blockedByPreviousFailure)
        return '🚫 Modul Sebelumnya Gagal';

    if (blockedByPreviousNotFinished)
        return '⏳ Selesaikan Ujian Sebelumnya';
    if (['finished', 'failed'].includes(exam.session_status)) {
  return '📄 Lihat Hasil';
    }

    if (exam.session_status === 'disqualified') {
      return '🚫 Didiskualifikasi';
    }

const scheduleState = getScheduleState(exam, nowMs);

    if (scheduleState === 'upcoming') return '🕐 Belum Waktunya';
    if (scheduleState === 'expired') return '⛔ Waktu Sudah Berakhir';

    if (exam.session_status === 'active') return '▶ Lanjutkan Ujian';
    return '🚀 Mulai Ujian';
  };

  const handleButton = (exam, blockedByPreviousFailure) => {
    if (!isExamUnlocked(exam, blockedByPreviousFailure)) return;

    if (['finished', 'failed', 'disqualified'].includes(exam.session_status)) {
      navigate(`/result/${exam.session_id}`);
      return;
    }
    startExam(exam.id);
  };

 const orderedModulesRaw = getOrderedModules();

let previousLockReason = null;

const orderedModules = orderedModulesRaw.map((m) => {

    const blockedByPreviousFailure =
        previousLockReason === 'failed';

    const blockedByPreviousNotFinished =
        previousLockReason === 'not_finished';
    if (m.type === 'psychotest') {

        if (!psychotestStatus.completed) {
            previousLockReason = 'not_finished';
        }

    } else {

        const status = m.exam.session_status;

        if (status === 'failed' || status === 'disqualified') {
            previousLockReason = 'failed';
        }
        else if (status !== 'finished') {
            previousLockReason = 'not_finished';
        }

    }

    return {
        ...m,
        blockedByPreviousFailure,
        blockedByPreviousNotFinished
    };
});

  // ─── Render kartu psikotest ───────────────────────────────────────────────────
  const renderPsychotestCard = (blockedByPreviousFailure) => {
    const disqualified = psychotestStatus.module_status === 'disqualified';
    const psychotestInProgress = psychotestStatus.module_status === 'active';
    const psychotestUnlocked = isPsychotestUnlocked(blockedByPreviousFailure);
    const isLocked = disqualified || blockedByPreviousFailure || (!psychotestStatus.completed && !psychotestUnlocked);

    return (
      <div
        className={`exam-card psychotest-card ${
          psychotestStatus.completed ? 'psychotest-completed' : ''
        } ${isLocked ? 'exam-card-locked' : ''}`}
        key="psychotest"
      >
        {disqualified && (
          <div className="lock-overlay">
            <span className="lock-icon">🚫</span>
            <p>{psychotestStatus.result?.reason || 'Anda didiskualifikasi dari psikotest'}</p>
          </div>
        )}

        {!disqualified && blockedByPreviousFailure && !psychotestStatus.completed && (
          <div className="lock-overlay">
            <span className="lock-icon">🚫</span>
            <p>Modul sebelumnya tidak lulus/didiskualifikasi, sehingga psikotest ini tidak dapat dikerjakan.</p>
          </div>
        )}

        {!disqualified && !blockedByPreviousFailure && !psychotestStatus.completed && !psychotestUnlocked && (
          <div className="lock-overlay">
            <span className="lock-icon">
              {getScheduleState(psychotestStatus, nowMs) === 'expired' ? '⛔' : '🕐'}
            </span>
            <p>
              {getScheduleState(psychotestStatus, nowMs) === 'expired'
                ? 'Waktu psikotest telah berakhir'
                : 'Menunggu jadwal psikotest dibuka'}
            </p>
          </div>
        )}

        <div className="exam-card-header">
          <div className="exam-icon">{disqualified ? '🚫' : '🧠'}</div>
          <div className="exam-card-title">
            <h2>Psikotest Pra-Exam</h2>
            <span
              className={`exam-badge ${
                disqualified
                  ? 'badge-disq'
                  : psychotestStatus.completed
                  ? 'badge-finished'
                  : 'badge-active'
              }`}
            >
              {disqualified
                ? '🚫 Didiskualifikasi'
                : psychotestStatus.completed
                ? '✅ Selesai'
                : psychotestInProgress
                ? '⏳ Sedang Berlangsung'
                : '⏳ Wajib Dikerjakan'}
            </span>
          </div>
        </div>

        <p className="exam-desc">
          {disqualified
            ? 'Anda didiskualifikasi dari psikotest karena melanggar aturan pengerjaan.'
            : blockedByPreviousFailure
            ? 'Modul sebelumnya tidak lulus/didiskualifikasi.'
            : psychotestStatus.completed
            ? 'Psikotest sudah selesai dan hasil sudah direkam.'
            : psychotestInProgress
            ? 'Psikotest sedang berlangsung. Lanjutkan untuk meneruskan jawaban yang sudah tersimpan.'
            : 'Kerjakan psikotest sesuai jadwal yang tersedia.'}
        </p>

        {!disqualified && !blockedByPreviousFailure && !psychotestStatus.completed && renderScheduleInfo(psychotestStatus)}

        <div className="exam-info">
          <div className="info-item">
            <span className="info-label">📌 Status</span>
            <span className="info-val">
              {disqualified
                ? 'Didiskualifikasi'
                : psychotestStatus.completed
                ? 'Selesai'
                : psychotestInProgress
                ? 'Sedang Berlangsung'
                : 'Belum Selesai'}
            </span>
          </div>
        </div>

  {!disqualified &&
  psychotestStatus.completed &&
  showPsychotestResult &&
  (psychotestStatus.result?.groups || []).length > 0 && (
    <div className="rules-box" style={{ overflowX: 'auto' }}>
      <h3>📊 Ringkasan Hasil Psikotest</h3>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
        marginTop: '12px',
      }}>
        <thead>
          <tr style={{ backgroundColor: '#0d7a7a' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left', width: '160px', color: '#fff' }}>Kategori</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff' }}>Dimensi</th>
            <th style={{ padding: '8px 12px', textAlign: 'left', color: '#fff' }}>Kecenderungan</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', width: '80px', color: '#fff' }}>Skor</th>
          </tr>
        </thead>
        <tbody>
          {[...(psychotestStatus.result?.groups || [])]
  .sort((a, b) => {
    const order = {
      'HIGH ANALISYS': 0,
      'MIDDLE RANGE': 1,
      'LOW ANALISYS': 2,
    };

    return (order[a.label] ?? 3) - (order[b.label] ?? 3);
  })
  .map((g, gIdx) => {
            const levelOrder = { 'tinggi': 0, 'sedang-tinggi': 1, 'sedang': 2, 'sedang-rendah': 3, 'rendah': 4 };
            const sorted = [...(g.members || [])].sort(
              (a, b) => (levelOrder[a.level] ?? 5) - (levelOrder[b.level] ?? 5)
            );

            const highMembers   = sorted.filter(m => m.score >= 6);
            const middleMembers = sorted.filter(m => m.score >= 3.5 && m.score < 6);
            const lowMembers    = sorted.filter(m => m.score < 3.5);

            const groupLabel = g.label; 
            const groupColor = groupLabel === 'HIGH ANALISYS'
              ? '#dcfce7' : groupLabel === 'MIDDLE RANGE'
              ? '#fef9c3' : '#fee2e2';
            const groupTextColor = groupLabel === 'HIGH ANALISYS'
              ? '#166534' : groupLabel === 'MIDDLE RANGE'
              ? '#854d0e' : '#991b1b';

            const rowBg = gIdx % 2 === 0 ? '#f8fafc' : '#fff';
            const totalRows = sorted.length;

            const rows = [];

            const sections = [
              { label: 'HIGH ANALISYS', members: highMembers, bg: '#dcfce7', color: '#166534' },
              { label: 'MIDDLE RANGE',  members: middleMembers, bg: '#fef9c3', color: '#854d0e' },
              { label: 'LOW ANALISYS',  members: lowMembers,   bg: '#fee2e2', color: '#991b1b' },
            ].filter(s => s.members.length > 0);

            let firstRowInGroup = true;

            sections.forEach((section) => {
              section.members.forEach((m, mIdx) => {
                const isFirstInGroup = firstRowInGroup;
                firstRowInGroup = false;
                const isFirstInSection = mIdx === 0;

                rows.push(
                  <tr key={`${g.key}-${section.label}-${m.key}`} style={{ backgroundColor: rowBg, borderBottom: '1px solid #e2e8f0' }}>
                    {/* Kolom Kategori — hanya di baris pertama grup */}
                    {isFirstInGroup && (
                      <td
                        rowSpan={totalRows}
                        style={{
                          padding: '8px 12px',
                          fontWeight: '700',
                          verticalAlign: 'top',
                          borderRight: '2px solid #e2e8f0',
                          backgroundColor: groupColor,
                          color: groupTextColor,
                          fontSize: '12px',
                        }}
                      >
                        <div>{g.title}</div>
                        <div style={{
                          marginTop: '6px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          backgroundColor: groupColor,
                          border: `1px solid ${groupTextColor}`,
                          fontSize: '11px',
                          display: 'inline-block',
                        }}>
                          {g.label}
                        </div>
                        <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                          Rata-rata: {g.averageScore}
                        </div>
                      </td>
                    )}

                    {/* Dimensi */}
                    <td style={{
                      padding: '7px 12px',
                      fontWeight: isFirstInSection ? '600' : '400',
                      borderRight: '1px solid #e2e8f0',
                      fontSize: '12px',
                    }}>
                      {/* Label HIGH/MIDDLE/LOW di dimensi pertama section */}
                      {isFirstInSection && (
                        <span style={{
                          display: 'inline-block',
                          marginBottom: '4px',
                          padding: '1px 6px',
                          borderRadius: '10px',
                          backgroundColor: section.bg,
                          color: section.color,
                          fontSize: '10px',
                          fontWeight: '700',
                          border: `1px solid ${section.color}`,
                        }}>
                          {section.label}
                        </span>
                      )}
                      <div>{m.title}</div>
                    </td>

                    {/* Tendency */}
                    <td style={{
                      padding: '7px 12px',
                      color: '#374151',
                      borderRight: '1px solid #e2e8f0',
                      fontSize: '12px',
                      lineHeight: '1.4',
                    }}>
                      {m.tendency}
                    </td>

                    {/* Score */}
                    <td style={{
                      padding: '7px 12px',
                      textAlign: 'center',
                      fontWeight: '700',
                      color: m.score >= 6 ? '#166534' : m.score >= 3.5 ? '#854d0e' : '#991b1b',
                      fontSize: '13px',
                    }}>
                      {m.score}/9
                    </td>
                  </tr>
                );
              });
            });

            return rows;
          })}
        </tbody>
      </table>
    </div>
  )}

        {!disqualified &&
          psychotestStatus.completed &&
          showPsychotestResult &&
          (psychotestStatus.result?.dimensions || []).length === 0 && (
            <div className="rules-box">
              <h3>📊 Ringkasan Hasil Psikotest</h3>
              <p>Hasil psikotest belum tersedia.</p>
            </div>
          )}

        <button
          className={`btn-start ${
            psychotestStatus.completed ? 'btn-secondary-start' : ''
          } ${disqualified || blockedByPreviousFailure ? 'btn-disq' : ''} ${
            !disqualified && !blockedByPreviousFailure && !psychotestUnlocked && !psychotestStatus.completed
              ? 'btn-locked'
              : ''
          }`}
          style={{ maxWidth: '360px' }}
          disabled={
            disqualified || blockedByPreviousFailure || (!psychotestStatus.completed && !psychotestUnlocked)
          }
          onClick={() => {
            if (disqualified || blockedByPreviousFailure) return;
            if (psychotestStatus.completed) {
              setShowPsychotestResult((prev) => !prev);
            } else {
              navigate('/psychotest');
            }
          }}
        >
          {disqualified
            ? '🚫 Didiskualifikasi'
            : blockedByPreviousFailure
            ? '🚫 Modul Sebelumnya Gagal'
            : psychotestStatus.completed
            ? showPsychotestResult
              ? 'Sembunyikan Hasil Psikotest'
              : 'Lihat Hasil Psikotest'
            : psychotestUnlocked
            ? psychotestInProgress
              ? 'Lanjutkan Psikotest'
              : 'Mulai Psikotest'
            : getScheduleState(psychotestStatus, nowMs) === 'expired'
            ? '⛔ Waktu Sudah Berakhir'
            : '🕐 Belum Waktunya'}
        </button>
      </div>
    );
  };

  // ─── Render kartu exam ─────────────────────────────────────────────────────────
  const renderExamCard = (
    exam,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
) => {
    const unlocked = isExamUnlocked(
        exam,
        blockedByPreviousFailure || blockedByPreviousNotFinished
    );
    const isStarting = startingId === exam.id;
    const isBlocked = !unlocked;
    const scheduleState = getScheduleState(exam, nowMs);

    let lockMessage = null;
    if (blockedByPreviousFailure) {

    lockMessage =
        'Modul sebelumnya tidak lulus atau didiskualifikasi, sehingga ujian ini tidak dapat dikerjakan.';

}
else if (blockedByPreviousNotFinished) {

    lockMessage =
        'Selesaikan ujian sebelumnya terlebih dahulu.';

} else if (scheduleState === 'upcoming') {
      lockMessage = `Dibuka pada ${formatDateTime(exam.scheduled_start_at)}`;
    } else if (scheduleState === 'expired') {
      lockMessage = 'Waktu ujian ini telah berakhir';
    } else if (exam.is_available === false) {
      lockMessage = exam.availability_reason || 'Ujian ini belum tersedia';
    }

    return (
      <div className={`exam-card ${isBlocked ? 'exam-card-locked' : ''}`} key={exam.id}>
        {isBlocked && (
          <div className="lock-overlay">
            <span className="lock-icon">
              {blockedByPreviousFailure
                ? '🚫'
                : scheduleState === 'expired'
                ? '⛔'
                : scheduleState === 'upcoming'
                ? '🕐'
                : '🔒'}
            </span>
            <p>{lockMessage}</p>
          </div>
        )}

        <div className="exam-card-header">
          <div className="exam-icon">{isBlocked ? '🔒' : '📝'}</div>
          <div className="exam-card-title">
            <h2>{exam.title}</h2>
            {getStatusBadge(exam.session_status)}
          </div>
        </div>

        {exam.description && <p className="exam-desc">{exam.description}</p>}

        {!['finished', 'failed', 'disqualified'].includes(exam.session_status) &&
  !blockedByPreviousFailure &&
  renderScheduleInfo(exam)}

        <div className="exam-info">
          <div className="info-item">
            <span className="info-label">⏱ Durasi</span>
            <span className="info-val">{formatDuration(exam.duration_minutes)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">❓ Soal</span>
            <span className="info-val">{exam.total_questions} Pertanyaan</span>
          </div>
          <div className="info-item">
            <span className="info-label">⚠️ Maks. Pelanggaran</span>
            <span className="info-val">{exam.max_violations} Kali</span>
          </div>
        </div>

        <div className="rules-box">
          <h3>⚠️ Peraturan Ujian</h3>
          <ul>
            <li>Ujian wajib dilakukan dalam mode layar penuh</li>
            <li>Dilarang berpindah tab atau keluar jendela browser</li>
            <li>Kamera wajah harus aktif selama ujian</li>
            <li>Wajah harus terdeteksi — jika hilang &gt;5 detik = pelanggaran</li>
            <li>Lebih dari <strong>{exam.max_violations} pelanggaran</strong> akan didiskualifikasi</li>
            <li>Copy-paste dinonaktifkan selama ujian</li>
            <li>Jawaban tersimpan otomatis</li>
          </ul>
        </div>

        <button
          className={`btn-start ${!unlocked ? 'btn-locked' : ''} ${
            exam.session_status === 'disqualified' || blockedByPreviousFailure ? 'btn-disq' : ''
          }`}
          onClick={() =>
              handleButton(
                  exam,
                  blockedByPreviousFailure || blockedByPreviousNotFinished
              )
          }
          disabled={
    isStarting ||
    !unlocked ||
    exam.session_status === 'disqualified' ||
    blockedByPreviousFailure ||
    blockedByPreviousNotFinished
}
        >
          {getButtonLabel(
    exam,
    isStarting,
    blockedByPreviousFailure,
    blockedByPreviousNotFinished
)}
        </button>
      </div>
    );
  };
      if (!profileChecked) {
  return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Memuat...</p>
    </div>
  );
}

// if (needsProfile) {
//   return <ProfileModal onComplete={() => setNeedsProfile(false)} />;
// }
  return (
    <div className="dashboard">
      <header className="dash-header">
        <div className="dash-logo">📋 Sistem Ujian Rekrutmen</div>
        <div className="dash-user">
          <span>👤 {user?.name}</span>
          <button onClick={logout} className="btn-logout">
            Keluar
          </button>
        </div>
      </header>

      <main className="dash-main">
        <div className="welcome-card">
          <h1>Selamat Datang, {user?.name}!</h1>
          <p>Anda terdaftar sebagai peserta ujian rekrutmen.</p>
        </div>

        {error && <div className="alert-error">{error}</div>}

      {needsProfile && (
        <div className="profile-modal-overlay">
          <ProfileModal onComplete={() => setNeedsProfile(false)} />
        </div>
      )}

        {loadingExams ? (
          <div className="loading-screen">
            <div className="spinner"></div>
            <p>Memuat daftar ujian...</p>
          </div>
        ) : orderedModules.length === 0 ? (
  <div className="empty-state">
    <p>Tidak ada ujian yang tersedia saat ini.</p>
  </div>
) : (
          <div className="exam-list">
            {orderedModules.map((m) =>
              m.type === 'psychotest'
                ? renderPsychotestCard(m.blockedByPreviousFailure)
                : renderExamCard(
    m.exam,
    m.blockedByPreviousFailure,
    m.blockedByPreviousNotFinished
)
            )}
          </div>
        )}
      </main>
    </div>
  );
}